/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useField } from 'formik';
import classNames from 'classnames';
import { InputGroup, Form, Col } from 'react-bootstrap';
import arrowOpen from 'assets/img/select-arrow-open.svg';
import Label from '../Label';
import ErrorMessage from '../ErrorMessage';
import './Input.scss';

const Input = React.forwardRef(
  (
    {
      label = '',
      hasValidation = true,
      maxLength = 100,
      type = 'text',
      placeholder = '',
      as: FormControl = Form.Control,
      inputMode = '',
      containerClassName = '',
      labelClassName = '',
      groupClassName = '',
      onBlur,
      onChange,
      right = null,
      className = '',
      readonly = false,
      mask,
      ...props
    },
    ref
  ) => {
    const [field, meta, helpers] = useField(props);

    const { error, touched } = meta;

    const isNumber = type === 'number';
    const inputValue = field.value;
    const minValue = Number(props.min);
    const maxValue = Number(props.max);
    const stopServiceForMax = inputValue === maxValue;
    const stopServiceForMin = inputValue === minValue;

    const onBeforeInput = (e) => {
      if (inputMode !== 'numeric' && inputMode !== 'name') return;

      if (inputMode === 'numeric' && !/[0-9]/.test(e.data)) {
        e.preventDefault();
      }

      if (inputMode === 'name' && !/[a-zA-Z\u00C0-\u017F ]/.test(e.data)) {
        e.preventDefault();
      }
    };

    const getEventValue = (e) => {
      const { value } = e.target;

      const evt = {
        ...e,
        target: {
          ...e.target,
          value: isNumber && value ? Number(value) : value
        }
      };
      return evt;
    };

    const handleChange = (e) => {
      const evt = getEventValue(e);

      if (mask) {
        const { value } = evt.target;

        const newValue =
          typeof mask === 'function' ? mask(value) : mask?.apply(value);
        evt.target.value = newValue;
        e.target.value = newValue;
      }

      if (Number(e.target.value) < 0 || Number(e.target.value) > maxValue) {
        return false;
      }

      field.onChange(e);

      if (onChange) onChange(evt);
    };

    const handleBlur = (e) => {
      const evt = getEventValue(e);

      field.onBlur(evt);

      if (onBlur) onBlur(evt);
    };

    const handleChangeNumberValue = useCallback(
      (numberType) => {
        const currentValue = Number(field.value);

        const increment = () => {
          const incrementValue =
            Number.isNaN(currentValue) || currentValue < 1
              ? 1
              : maxValue
              ? currentValue + 1 <= maxValue
                ? currentValue + 1
                : currentValue
              : currentValue + 1;

          return incrementValue;
        };

        const decrease = () => {
          const decreaseValue =
            Number.isNaN(currentValue) || currentValue < 1
              ? 0
              : minValue
              ? currentValue - 1 >= minValue
                ? currentValue - 1
                : currentValue
              : currentValue - 1;
          helpers.setValue(decreaseValue);

          return decreaseValue;
        };

        const value = numberType === 'increment' ? increment() : decrease();

        if (onChange) onChange({ target: { value } });
      },
      [field, helpers, onChange]
    );

    return (
      <InputGroup
        hasValidation={hasValidation}
        className={classNames('Input my-2', groupClassName)}
      >
        <Label label={label} className={labelClassName} />

        <Col
          xs={12}
          className={classNames(
            containerClassName,
            isNumber && 'disabledNativeNumberInput',
            right && 'd-flex'
          )}
        >
          <FormControl
            {...field}
            {...props}
            ref={ref}
            onBlur={handleBlur}
            onChange={handleChange}
            type={type}
            as="input"
            placeholder={placeholder || label}
            maxLength={maxLength}
            isInvalid={touched && error}
            onBeforeInput={onBeforeInput}
            className={classNames(
              isNumber && 'Input__Number',
              readonly && 'readonly',
              className
            )}
            readOnly={readonly ? 'readonly' : undefined}
          />
          {isNumber && !props.disabled && (
            <div className="Input__Increment_buttons">
              <img
                className="top-arrow"
                src={arrowOpen}
                alt="Incrementar"
                onClick={() =>
                  stopServiceForMax
                    ? null
                    : handleChangeNumberValue('increment')
                }
              />
              <img
                className="bottom-arrow"
                src={arrowOpen}
                alt="Restar"
                onClick={() =>
                  stopServiceForMin ? null : handleChangeNumberValue('decrease')
                }
              />
            </div>
          )}
          {right && <span className="Input__Right">{right}</span>}
        </Col>

        <ErrorMessage {...meta} className="text-right" />
      </InputGroup>
    );
  }
);

Input.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  hasValidation: PropTypes.bool,
  maxLength: PropTypes.number,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  as: PropTypes.node,
  inputMode: PropTypes.string,
  containerClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  groupClassName: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  right: PropTypes.node,
  className: PropTypes.string,
  readonly: PropTypes.bool,
  mask: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      apply: PropTypes.func
    })
  ])
};

export default Input;
